import React, { useState, useEffect } from "react";
import { apiEndpoints, adminFetch } from "../config/api";
import {
  FiStar,
  FiRefreshCw,
  FiCheck,
  FiX,
  FiEye,
  FiTrash2,
} from "react-icons/fi";
import { format } from "date-fns";

const ReviewsPanel = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all, pending, published
  const [selectedReview, setSelectedReview] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Helper function to get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    // If already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    // If relative path, use apiEndpoints helper to prepend base URL
    return apiEndpoints.images.url(imagePath);
  };

  // Fetch all reviews
  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminFetch(apiEndpoints.reviews.adminAll);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch reviews: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setReviews(data.data);
      } else {
        setReviews([]);
        setError(data.message || "No reviews found");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching reviews:", err);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  // Load reviews on component mount
  useEffect(() => {
    fetchReviews();
  }, []);

  const handleView = (review) => {
    setSelectedReview(review);
    setIsViewModalOpen(true);
  };

  const handleStatusChange = async (reviewId, newStatus) => {
    try {
      if (newStatus === "published") {
        // Call approve API
        const approveUrl = apiEndpoints.reviews.approve(reviewId);
        const requestBody = {
          publish: true,
          status: "published"
        };
        
        console.log("🔐 Approving review:", approveUrl);
        console.log("📦 Request body:", requestBody);
        
        const response = await adminFetch(approveUrl, {
          method: "PATCH",
          body: JSON.stringify(requestBody),
        });

        console.log("📡 Approve response status:", response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error("❌ Approve error:", errorData);
          throw new Error(errorData.message || `Failed to approve review: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log("✅ Approve response:", data);
        
        if (data.success) {
          // Update the review in the list
          setReviews(prevReviews =>
            prevReviews.map(review =>
              review._id === reviewId
                ? { 
                    ...review, 
                    status: "published", 
                    isPublished: true,
                    ...(data.review && { ...data.review })
                  }
                : review
            )
          );
          setError(null);
        } else {
          throw new Error(data.message || "Failed to approve review");
        }
      } else {
        // For unpublish, update locally (or implement unpublish API if available)
        setReviews(prevReviews =>
          prevReviews.map(review =>
            review._id === reviewId
              ? { ...review, status: newStatus, isPublished: false }
              : review
          )
        );
      }
    } catch (err) {
      console.error("Error updating review status:", err);
      const errorMessage = err.message || "Failed to update review status. Please try again.";
      setError(errorMessage);
      alert(errorMessage);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review?")) {
      return;
    }

    try {
      setError(null);
      const response = await adminFetch(apiEndpoints.reviews.delete(reviewId), {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete review: ${response.status}`);
      }

      setReviews((prevReviews) => prevReviews.filter((review) => review._id !== reviewId));
      if (selectedReview?._id === reviewId) {
        setIsViewModalOpen(false);
        setSelectedReview(null);
      }
    } catch (err) {
      console.error("Error deleting review:", err);
      setError(err.message || "Failed to delete review. Please try again.");
      alert(err.message || "Failed to delete review. Please try again.");
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: "Pending", className: "ui-mdc-booking-status-badge--pending" },
      published: { label: "Published", className: "ui-mdc-booking-status-badge--confirmed" },
      rejected: { label: "Rejected", className: "ui-mdc-booking-status-badge--cancelled" },
    };
    
    const statusInfo = statusMap[status] || statusMap.pending;
    return (
      <span className={`ui-mdc-booking-status-badge ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            size={16}
            fill={star <= rating ? "#FFD700" : "none"}
            stroke={star <= rating ? "#FFD700" : "#ccc"}
          />
        ))}
        <span style={{ marginLeft: "8px", fontSize: "0.9rem", color: "rgba(255, 255, 255, 0.7)" }}>
          {rating}/5
        </span>
      </div>
    );
  };

  const filteredReviews = filter === "all" 
    ? reviews 
    : reviews.filter(review => {
        if (filter === "pending") return review.status === "pending";
        if (filter === "published") return review.status === "published" || review.isPublished;
        return true;
      });

  if (loading) {
    return (
      <div className="ui-mdc-package-loading">
        <div className="ui-mdc-package-loading-spinner"></div>
        <span>Loading reviews...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header Section */}
      <div className="ui-mdc-package-header">
        <h1 className="ui-mdc-package-title">Reviews Management</h1>
        <button
          onClick={fetchReviews}
          className="ui-mdc-package-button ui-mdc-package-button--secondary mt-5"
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "animate-spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="ui-mdc-booking-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="ui-mdc-package-filters">
        {[
          { id: "all", label: "All Reviews" },
          { id: "pending", label: "Pending" },
          { id: "published", label: "Published" },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`ui-mdc-package-filter ${
              filter === f.id ? "ui-mdc-package-filter--active" : ""
            }`}
          >
            {f.label}
            {f.id !== "all" && (
              <span style={{ 
                marginLeft: "8px",
                padding: "2px 8px",
                borderRadius: "12px",
                backgroundColor: filter === f.id ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.1)",
                fontSize: "0.75rem"
              }}>
                {reviews.filter(r => 
                  f.id === "pending" ? r.status === "pending" : 
                  f.id === "published" ? (r.status === "published" || r.isPublished) : false
                ).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Reviews Grid */}
      {!loading && (
        <div className="ui-mdc-package-grid mt-5">
          {filteredReviews.length === 0 ? (
            <div className="ui-mdc-package-empty">
              <p className="ui-mdc-package-empty-title">No reviews found</p>
            </div>
          ) : (
            filteredReviews.map((review) => (
              <div key={review._id} className="ui-mdc-package-item">
                <div className="ui-mdc-package-item-header">
                  <div style={{ flex: 1 }}>
                    <h3 className="ui-mdc-package-item-title">{review.customerName}</h3>
                    <p className="ui-mdc-package-item-subtitle">{review.customerEmail}</p>
                    <p style={{ fontSize: "0.8rem", color: "rgba(255, 255, 255, 0.5)", marginTop: "4px" }}>
                      {review.bookingReference}
                    </p>
                  </div>
                  <div>
                    {getStatusBadge(review.status)}
                  </div>
                </div>

                {/* Rating */}
                <div style={{ margin: "1rem 0" }}>
                  {renderStars(review.rating)}
                </div>

                {/* Review Message */}
                <p className="ui-mdc-package-item-description" style={{ 
                  maxHeight: "100px",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}>
                  {review.reviewMessage}
                </p>

                {/* Review Meta */}
                <div className="ui-mdc-package-item-meta">
                  <div className="ui-mdc-package-item-meta-item">
                    <span className="ui-mdc-package-item-meta-label">Service</span>
                    <span className="ui-mdc-package-item-meta-value" style={{ textTransform: "capitalize" }}>
                      {review.serviceType}
                    </span>
                  </div>
                  <div className="ui-mdc-package-item-meta-item">
                    <span className="ui-mdc-package-item-meta-label">Date</span>
                    <span className="ui-mdc-package-item-meta-value">
                      {format(new Date(review.createdAt), "MMM dd, yyyy")}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="ui-mdc-package-item-actions">
                  <button
                    onClick={() => handleView(review)}
                    className="ui-mdc-package-button ui-mdc-package-button--secondary"
                  >
                    <FiEye style={{ marginRight: "0.5rem" }} />
                    View
                  </button>
                  {review.status === "pending" && (
                    <button
                      onClick={() => handleStatusChange(review._id, "published")}
                      className="ui-mdc-package-button ui-mdc-package-button--primary"
                    >
                      <FiCheck style={{ marginRight: "0.5rem" }} />
                      Publish
                    </button>
                  )}
                  {review.status === "published" && (
                    <button
                      onClick={() => handleStatusChange(review._id, "pending")}
                      className="ui-mdc-package-button ui-mdc-package-button--secondary"
                    >
                      Unpublish
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(review._id)}
                    className="ui-mdc-package-button ui-mdc-package-button--danger"
                  >
                    <FiTrash2 style={{ marginRight: "0.5rem" }} />
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* View Review Modal */}
      {isViewModalOpen && selectedReview && (
        <div className="ui-mdc-confirm-modal">
          <div className="ui-mdc-confirm-modal-content ui-mdc-booking-view-modal">
            <div className="ui-mdc-confirm-modal-header">
              <h3 className="ui-mdc-confirm-modal-title">Review Details</h3>
              <button
                type="button"
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedReview(null);
                }}
                className="ui-mdc-confirm-modal-close"
                aria-label="Close modal"
              >
                <FiX />
              </button>
            </div>
            <div className="ui-mdc-booking-view-content" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {/* Customer Information */}
              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Customer Information</h4>
                <div className="ui-mdc-booking-view-grid">
                  <div className="ui-mdc-booking-view-item">
                    <label>Name</label>
                    <p>{selectedReview.customerName}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Email</label>
                    <p>{selectedReview.customerEmail}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Phone</label>
                    <p>{selectedReview.customerPhone}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Booking Reference</label>
                    <p>{selectedReview.bookingReference}</p>
                  </div>
                </div>
              </div>

              {/* Review Content */}
              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Review</h4>
                <div className="ui-mdc-booking-view-grid">
                  <div className="ui-mdc-booking-view-item">
                    <label>Rating</label>
                    <div>{renderStars(selectedReview.rating)}</div>
                  </div>
                  <div className="ui-mdc-booking-view-item" style={{ gridColumn: "1 / -1" }}>
                    <label>Review Message</label>
                    <p style={{ whiteSpace: "pre-wrap" }}>{selectedReview.reviewMessage}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Service Type</label>
                    <p style={{ textTransform: "capitalize" }}>{selectedReview.serviceType}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Status</label>
                    <div>{getStatusBadge(selectedReview.status)}</div>
                  </div>
                </div>
              </div>

              {/* Images */}
              {(selectedReview.customerPhoto || selectedReview.customerSignature) && (
                <div className="ui-mdc-booking-view-section">
                  <h4 className="ui-mdc-booking-view-section-title">Attachments</h4>
                  <div className="ui-mdc-booking-view-grid">
                    {selectedReview.customerPhoto && (
                      <div className="ui-mdc-booking-view-item" style={{ gridColumn: "1 / -1" }}>
                        <label>Customer Photo</label>
                        <div style={{ marginTop: "8px" }}>
                          <img
                            src={getImageUrl(selectedReview.customerPhoto)}
                            alt="Customer"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "300px",
                              borderRadius: "8px",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "block";
                            }}
                          />
                          <p style={{ display: "none", color: "rgba(255, 255, 255, 0.5)" }}>
                            Image not available
                          </p>
                        </div>
                      </div>
                    )}
                    {selectedReview.customerSignature && (
                      <div className="ui-mdc-booking-view-item" style={{ gridColumn: "1 / -1" }}>
                        <label>Customer Signature</label>
                        <div style={{ marginTop: "8px" }}>
                          <img
                            src={getImageUrl(selectedReview.customerSignature)}
                            alt="Signature"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "200px",
                              borderRadius: "8px",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              backgroundColor: "#fff",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "block";
                            }}
                          />
                          <p style={{ display: "none", color: "rgba(255, 255, 255, 0.5)" }}>
                            Signature not available
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="ui-mdc-booking-view-section">
                <h4 className="ui-mdc-booking-view-section-title">Metadata</h4>
                <div className="ui-mdc-booking-view-grid">
                  <div className="ui-mdc-booking-view-item">
                    <label>Review ID</label>
                    <p>{selectedReview.reviewId}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Created At</label>
                    <p>
                      {format(new Date(selectedReview.createdAt), "MMM dd, yyyy HH:mm")}
                    </p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Updated At</label>
                    <p>
                      {format(new Date(selectedReview.updatedAt), "MMM dd, yyyy HH:mm")}
                    </p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Views</label>
                    <p>{selectedReview.views || 0}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Helpful Count</label>
                    <p>{selectedReview.helpfulCount || 0}</p>
                  </div>
                  <div className="ui-mdc-booking-view-item">
                    <label>Verified</label>
                    <p>{selectedReview.isVerified ? "Yes" : "No"}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="ui-mdc-confirm-modal-actions">
              <button
                type="button"
                onClick={() => {
                  setIsViewModalOpen(false);
                  setSelectedReview(null);
                }}
                className="ui-mdc-confirm-button ui-mdc-confirm-button--secondary"
              >
                Close
              </button>
              {selectedReview.status === "pending" && (
                <button
                  type="button"
                  onClick={() => {
                    handleStatusChange(selectedReview._id, "published");
                    setIsViewModalOpen(false);
                    setSelectedReview(null);
                  }}
                  className="ui-mdc-confirm-button"
                  style={{ backgroundColor: "#c4915f", color: "white" }}
                >
                  <FiCheck style={{ marginRight: "0.5rem" }} />
                  Publish Review
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsPanel;

